import React, { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { User } from '@supabase/supabase-js';

import { hasSupabaseConfig, supabase } from './api/supabaseClient';
import { Footer } from './components/layout/Footer';
import { Header } from './components/layout/Header';
import { ProfileSetupModal } from './components/common/ProfileSetupModal';
import { SignupCompletionModal } from './components/common/SignupCompletionModal';
import { AuthRedirectHandler } from './features/auth/AuthRedirectHandler';
import { CartModal } from './features/cart/components/CartModal';
import PaymentSuccess from './features/cart/components/PaymentSuccess';
import { CartProvider } from './features/cart/context/CartContext';
import { CommunityPage } from './features/community/CommunityPage';
import { PostDetailPage } from './features/community/PostDetailPage';
import { PostWritePage } from './features/community/PostWritePage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { PrivacyPolicyPage } from './features/legal/PrivacyPolicyPage';
import { SupportPage } from './features/legal/SupportPage';
import { TermsOfServicePage } from './features/legal/TermsOfServicePage';
import { MealPage } from './features/meal/MealPage';
import { NewsPage } from './features/news/NewsPage';
import { MyPage } from './features/profile/MyPage';
import { isProfileSetupRequired } from './features/profile/profileModes';
import { Profile } from './features/profile/types';
import { RecruitmentPage } from './features/recruitment/RecruitmentPage';
import { ArmedReservePage } from './features/reservation/ArmedReservePage';
import { ShopPage } from './features/shop/ShopPage';

type StorageEntry = {
  key: string;
  store: Storage;
};

const getSupabaseAuthStorageEntries = (): StorageEntry[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const stores = [window.localStorage, window.sessionStorage];
  const entries: StorageEntry[] = [];

  for (const store of stores) {
    for (const key of Object.keys(store)) {
      if (key.startsWith('sb-') || key.startsWith('supabase.auth.')) {
        entries.push({ key, store });
      }
    }
  }

  return entries;
};

const clearSupabaseAuthStorage = () => {
  for (const entry of getSupabaseAuthStorageEntries()) {
    entry.store.removeItem(entry.key);
  }
};

const hasSupabaseAuthStorage = () => getSupabaseAuthStorageEntries().length > 0;

const loadProfile = async (currentUser: User): Promise<Profile | null> => {
  if (!hasSupabaseConfig) {
    return null;
  }

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', currentUser.id)
    .maybeSingle();

  if (error) {
    console.error('[App] profile lookup failed:', error);
    return null;
  }

  return (data as Profile | null) ?? null;
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [profile, setProfile] = useState<Profile | null | undefined>(undefined);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showSignupCompletion, setShowSignupCompletion] = useState(false);

  useEffect(() => {
    let isActive = true;

    const syncInitialSession = async () => {
      if (!hasSupabaseConfig) {
        clearSupabaseAuthStorage();

        if (isActive) {
          setUser(null);
          setProfile(null);
          setShowProfileSetup(false);
        }
        return;
      }

      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (!isActive) {
        return;
      }

      if (error) {
        console.error('[App] getSession failed:', error);
      }

      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (!currentUser) {
        setProfile(null);
        setShowProfileSetup(false);
      }
    };

    void syncInitialSession();

    if (!hasSupabaseConfig) {
      return () => {
        isActive = false;
      };
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isActive) {
        return;
      }

      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (!currentUser) {
        setProfile(null);
        setShowProfileSetup(false);
      }
    });

    return () => {
      isActive = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const syncProfile = async () => {
      if (user === undefined) {
        return;
      }

      if (!user) {
        setProfile(null);
        setShowProfileSetup(false);
        return;
      }

      setProfile(undefined);
      setShowProfileSetup(false);

      const nextProfile = await loadProfile(user);
      if (!isActive) {
        return;
      }

      setProfile(nextProfile);
      setShowProfileSetup(isProfileSetupRequired(nextProfile));
    };

    void syncProfile();

    return () => {
      isActive = false;
    };
  }, [user]);

  const handleSignOut = async () => {
    if (!hasSupabaseConfig) {
      clearSupabaseAuthStorage();
      setUser(null);
      setProfile(null);
      setShowProfileSetup(false);
      return;
    }

    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      console.error('[App] signOut failed:', error);
    }

    clearSupabaseAuthStorage();

    const {
      data: { session },
    } = await supabase.auth.getSession();
    const hasResidualStorage = hasSupabaseAuthStorage();

    setUser(null);
    setProfile(null);
    setShowProfileSetup(false);

    if ((session || hasResidualStorage) && typeof window !== 'undefined') {
      console.error('[App] signOut verification failed:', {
        hasSession: Boolean(session),
        hasResidualStorage,
      });
      window.location.replace(window.location.pathname);
    }
  };

  if (user === undefined) {
    return null;
  }

  return (
    <CartProvider>
      <BrowserRouter>
        <AuthRedirectHandler user={user} />
        <div className="min-h-screen flex flex-col bg-surface dark:bg-slate-950 transition-colors">
          <Header user={user} profile={profile ?? null} onSignOut={handleSignOut} />
          <main className="mx-auto w-full max-w-7xl flex-grow px-4 pb-14 pt-24 sm:px-6 sm:pb-16 sm:pt-28 lg:px-8 lg:pb-20 lg:pt-32">
            <Routes>
              <Route path="/" element={<ShopPage />} />
              <Route path="/Dashboard" element={<DashboardPage profile={profile ?? null} />} />
              <Route path="/ArmedReserve" element={<ArmedReservePage />} />
              <Route path="/armed-reseve" element={<ArmedReservePage />} />
              <Route path="/recruitment" element={<RecruitmentPage />} />
              <Route path="/Meal" element={<MealPage />} />
              <Route path="/News" element={<NewsPage />} />
              <Route
                path="/MyPage"
                element={
                  <MyPage
                    user={user}
                    profile={profile ?? null}
                    isProfileLoading={profile === undefined}
                    onProfileUpdated={setProfile}
                    onAccountDeleted={handleSignOut}
                  />
                }
              />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/Community" element={<CommunityPage user={user} profile={profile ?? null} />} />
              <Route path="/Community/write" element={<PostWritePage user={user} profile={profile ?? null} />} />
              <Route path="/Community/:postId" element={<PostDetailPage user={user} profile={profile ?? null} />} />
              <Route path="/Community/:postId/edit" element={<PostWritePage user={user} profile={profile ?? null} />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/support" element={<SupportPage />} />
            </Routes>
          </main>
          <Footer />
        </div>
        <CartModal />
        {showProfileSetup && user ? (
          <ProfileSetupModal
            user={user}
            initialProfile={profile ?? null}
            onProfileCreated={(newProfile) => {
              const shouldShowCompletionMessage = profile == null;
              setProfile(newProfile);
              setShowProfileSetup(false);
              if (shouldShowCompletionMessage) {
                setShowSignupCompletion(true);
              }
            }}
            onSignOut={handleSignOut}
          />
        ) : null}
        <SignupCompletionModal
          isOpen={showSignupCompletion}
          onClose={() => setShowSignupCompletion(false)}
        />
      </BrowserRouter>
    </CartProvider>
  );
};

export default App;
